import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip, Table, Tag, Space, Button, Input, Modal, Descriptions, message, Popconfirm } from 'antd';
import { SearchOutlined, SyncOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { adminInstance } from './api/axiosConfig';
import config from '../config';
import WorkflowDetailsModal from './WorkflowDetailsModal'; 


const { Search } = Input;
const { confirm } = Modal;

const WorkflowQueue = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timezoneOffset, setTimezoneOffset] = useState(0);

  const utcToLocal = useCallback((utcDateString) => {
    if (!utcDateString) return 'Not scheduled';
    const date = new Date(utcDateString + 'Z');  // Append 'Z' to ensure it's treated as UTC
    date.setHours(date.getHours());
    return date.toLocaleString();
  }, [timezoneOffset]);

  const fetchExecutions = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/executions`, {
        params: { page, pageSize, search },
      });
      console.log('Fetched executions:', response.data);
      if (response.data && Array.isArray(response.data.executions)) {
        setExecutions(response.data.executions.map(execution => ({
          ...execution,
          key: execution.id,
          next_execution: utcToLocal(execution.next_execution)
        })));
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.total,
        }));
        setTimezoneOffset(response.data.timezone_offset || 0);
      } else {
        console.error('Invalid data structure received:', response.data);
        message.error('Received invalid data format from server');
      }
    } catch (error) {
      console.error('Error fetching executions:', error);
      message.error('Failed to fetch workflow executions');
    }
    setLoading(false);
  }, [utcToLocal]);

  useEffect(() => {
    fetchExecutions(pagination.current, pagination.pageSize, searchTerm);
  }, [fetchExecutions, pagination.current, pagination.pageSize, searchTerm]);

  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchExecutions(pagination.current, pagination.pageSize, searchTerm);
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, fetchExecutions, pagination.current, pagination.pageSize, searchTerm]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    fetchExecutions(pagination.current, pagination.pageSize, searchTerm);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => !prev);
  };

  const viewDetails = async (id) => {
    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/executions/${id}`);
      let execution = response.data;
      
      console.log('Raw execution data:', execution);
  
      // Define annotation node prefixes to exclude
      const annotationPrefixes = ['stickyNote-', 'textAnnotation-', 'shape-', 'sticky-', 'annotation-', ];
  
      // Helper function to check if a node is an annotation
      const isAnnotationNode = (nodeId) => 
        annotationPrefixes.some(prefix => nodeId.startsWith(prefix));
  
      // Handle output_data processing
      if (typeof execution.output_data === 'string') {
        try {
          execution.output_data = JSON.parse(execution.output_data);
        } catch (e) {
          console.error('Failed to parse output_data:', e);
          execution.output_data = {};
        }
      }
      
      // If output_data is already an array of node summaries, use it directly
      if (Array.isArray(execution.node_summary) && execution.node_summary.length > 0) {
        // Filter out invalid nodes and annotation nodes
        execution.node_summary = execution.node_summary.filter(node => 
          node && 
          node.node_id && 
          node.node_id.trim() !== '' && 
          node.node_id !== 'undefined' &&
          !isAnnotationNode(node.node_id)  // Check node_id prefix
        );
      } else {
        // Convert output_data object to node_summary array and filter out invalid and annotation nodes
        execution.node_summary = Object.entries(execution.output_data || {})
          .filter(([nodeId]) => 
            nodeId && 
            nodeId.trim() !== '' && 
            nodeId !== 'undefined' &&
            !isAnnotationNode(nodeId)  // Check node_id prefix
          )
          .map(([nodeId, nodeData]) => ({
            key: nodeId,
            node_id: nodeId,
            status: nodeData.type || 'Unknown',
            type: nodeData.type,
            message: nodeData.content || nodeData.message || 'No message'
          }));
      }
  
      console.log('Processed execution data:', execution);
  
      setSelectedExecution(execution);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching execution details:', error);
      message.error('Failed to fetch execution details');
    }
  };

  const handleDelete = async (id, status) => {
    const isProcessing = status === 'processing';
    
    confirm({
      title: `Are you sure you want to ${isProcessing ? 'stop and ' : ''}delete this execution?`,
      icon: <ExclamationCircleOutlined />,
      content: isProcessing 
        ? 'This will terminate the ongoing process and remove the record.' 
        : 'This will remove the execution record.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await adminInstance.delete(`${config.apiSettings.root}wp-ai-workflows/v1/executions/${id}`);
          message.success(`Execution ${isProcessing ? 'stopped and ' : ''}deleted successfully`);
          fetchExecutions(pagination.current, pagination.pageSize, searchTerm);
        } catch (error) {
          console.error('Error deleting execution:', error);
          message.error(`Failed to ${isProcessing ? 'stop and ' : ''}delete execution`);
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Workflow Name',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={
          status === 'completed' ? 'green' :
          status === 'processing' ? 'blue' :
          status === 'scheduled' ? 'orange' :
          status === 'error' ? 'red' : 'default'
        }>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      align: 'right',
      width: 120,
      render: (cost) => (
        <Tooltip title="Total cost of all AI operations">
          <Tag color="blue" style={{ minWidth: '80px', textAlign: 'center' }}>
            ${Number(cost || 0).toFixed(4)}
          </Tag>
        </Tooltip>
      ),
      sorter: (a, b) => a.total_cost - b.total_cost,
    },
    {
      title: 'Next Execution',
      dataIndex: 'next_execution',
      key: 'next_execution',
      render: (nextExecution) => nextExecution,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button onClick={() => viewDetails(record.id)}>View Details</Button>
          <Button 
            icon={<DeleteOutlined />} 
            danger
            onClick={() => handleDelete(record.id, record.status)}
          >
            {record.status === 'processing' ? 'Stop & Delete' : 'Delete'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1>Workflow Execution Queue</h1>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search by workflow name"
          onSearch={handleSearch}
          style={{ width: 300 }}
          enterButton
        />
        <Button onClick={handleRefresh} icon={<SyncOutlined />}>
          Refresh
        </Button>
        <Button
          type={autoRefresh ? 'primary' : 'default'}
          onClick={toggleAutoRefresh}
        >
          {autoRefresh ? 'Disable Auto-refresh' : 'Enable Auto-refresh'}
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={executions}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
      />
<WorkflowDetailsModal 
  execution={selectedExecution}
  visible={detailsModalVisible}
  onClose={() => setDetailsModalVisible(false)}
/>
    </div>
  );
};

export default WorkflowQueue;